/* eslint-disable react/forbid-prop-types,react/no-danger,no-return-assign */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withCookies, Cookies} from 'react-cookie';
import {Helmet} from "react-helmet";
import moment from 'moment';
import Headroom from 'react-headroom';
import postscribe from "postscribe";
import {twillBase} from '../../config/API';
import {trackEvent} from '../../helpers/listeners/analytics';
import {filterHTML, filterHTMLTwill, stripTags} from "../../helpers/filters";
import './Article.scss';
import Paragraph from "../../components/Paragraph";
import CareersSidebar from "../../components/Careers/CareersSidebar";
import ReactVideoModal from "../../components/ReactVideoModal/ReactVideoModal";
import KnowledgeCenterTopicsNav from "../../components/KnowledgeCenter/KnowledgeCenterTopicsNav";
// import KnowledgeCenterTopicsNav from "./KnowledgeCenterTopicsNav";

class Article extends Component {

    static propTypes = {
        fontOverride: PropTypes.string,
        cookies: PropTypes.instanceOf(Cookies).isRequired,
        id: PropTypes.string.isRequired,
        content: PropTypes.object,
        entityMetatags: PropTypes.object,
        title: PropTypes.string,
        subtitle: PropTypes.string,
        articleViewTitle: PropTypes.string,
        date: PropTypes.object,
        authors: PropTypes.string,
        keyword: PropTypes.array,
        knowledgeCenterItems: PropTypes.array,
        supportingImage: PropTypes.object,
        link: PropTypes.object,
        ctas: PropTypes.array,
        fieldArticleComponents: PropTypes.array,
        category: PropTypes.string,
        hideImageFromArticle: PropTypes.bool,
        type: PropTypes.string,
        overrideVideoTitle: PropTypes.string,
    };

    static defaultProps = {
        fontOverride: null,
        content: {processed: ''},
        entityMetatags: {},
        title: '',
        subtitle: '',
        articleViewTitle: null,
        date: {value: ''},
        authors: '',
        keyword: [],
        knowledgeCenterItems: [],
        supportingImage: {},
        link: { uri: '' },
        ctas: [],
        fieldArticleComponents: [],
        category: 'Article',
        hideImageFromArticle: false,
        type: 'knowledge',
        overrideVideoTitle: 'Watch Video'
    };

    static getKeywords = items => {
        let keywords = [];

        // Fetch new Twill-based keyword structure
        const twillKeywords = items.filter(item => item.tags).map(item => typeof item.tags === 'object' ? item.tags.map(subItem => subItem.name.trim()) : item.tags);
        if (twillKeywords.length) keywords = [...keywords, ...twillKeywords];

        // Flatten and deduplicate the array
        return keywords.flat().filter((value, index, self) => self.indexOf(value) === index);
    }


    state = {
        components: '',
        stylesLoaded: false,
        scriptsLoaded: false,
        titleSearch: '',
        modalIsOpen: false,
        fontSize: 'small'
    };

    async componentDidMount() {
        const fontSize = this.props.cookies.get('maarticlefontsize');

        if (fontSize) {
            this.changeFontSize(fontSize);
        }

        await this.loadExternalStylesheet();

        // Determine type of Article loading
        let articleType = 'article';
        if (window.location.pathname.startsWith('/careers/stories/')) articleType = 'story';

        // Update the API call depending on Model
        let urlPath = 'articleById';
        if (articleType === 'story') urlPath = 'storyById';

        // Fetch the Twill Page in order to fetch component renders
        const url = `${twillBase}/${urlPath}/${this.props.id}`.replace(/\/$/, '');
        await fetch(url).then(res => res.text()).then(data => {

            const parser = new DOMParser();
            const html = parser.parseFromString(data, 'text/html');

            const articleBody = html.getElementsByClassName('articleBody');
            const bodySelector = html.querySelector('.mx-auto');

            // Remove the article body as it is already rendered
            if (articleBody && articleBody.length > 0) {
                for (let i=0; i < articleBody.length; i += 1) {
                    bodySelector.removeChild(articleBody[i]);
                }
            }

            // Select what remains
            const innerHTML = bodySelector.innerHTML;

            const has404AndNotFound = bodySelector.innerHTML.includes("404") && bodySelector.innerHTML.includes("Not Found");


            if (innerHTML.length && !has404AndNotFound) {
                this.setState({
                    components: innerHTML
                })
            }

        }).catch(err => {
            console.log(err);
            return null;
        });

        await this.loadExternalScripts();
    }

    /**
     * Get Title from MetaTags, Fall back to Page Title
     *
     * @return {string}
     */
    getMetaTitle = () => {
        const {entityMetatags, title, articleViewTitle} = this.props;

        const metaTitle = Object.values(entityMetatags).filter(metaTag => metaTag.key === 'title');
        if (Object.keys(metaTitle).length && metaTitle[0]) {
            return metaTitle[0].value;
        }

        if (articleViewTitle) {
            return `${title} | MarketAxess`;
        }

        return `${title} | MarketAxess`;
    };

    getSubHeadline = () => {
        const {date, authors} = this.props;

        let subHeadline = '';

        if (authors) {
            subHeadline += `By ${authors}`;
        }

        if (date && date.value) {
            if (subHeadline.length) subHeadline += ` | `;
            subHeadline += moment(date.value).format('D MMMM YYYY');
        }

        return subHeadline;
    };

    loadExternalStylesheet = async () => {
        const manifestPath = '/external-app-css';
        const url = `${twillBase}${manifestPath}`.replace(/\/$/, '');

        await fetch(url).then(res => res.text()).then(filePath => {
            if (!this.state.stylesLoaded) {
                postscribe(document.getElementById(`#twArticle${this.props.id}`), `<link rel="stylesheet" href="${filePath}">`);
                this.setState({stylesLoaded: true});
            }
        }).catch(err => {
            console.log(err);
            return null;
        });
    };

    loadExternalScripts = async () => {
        const manifestPath = '/external-app-js';
        const url = `${twillBase}${manifestPath}`.replace(/\/$/, '');

        await fetch(url).then(res => res.text()).then(filePath => {
            if (!this.state.scriptsLoaded) {
                postscribe(document.getElementById(`#twArticle${this.props.id}`), `<script src="${filePath}"></script>`);
                this.setState({scriptsLoaded: true});
            }
        }).catch(err => {
            console.log(err);
            return null;
        });
    };

    changeFontSize = fontSize => {
        const currentFontSizeCookie = this.props.cookies.get('maarticlefontsize');

        if (!['small', 'medium', 'large'].includes(fontSize)) {
            return;
        }

        // Update Cookie
        if (fontSize !== currentFontSizeCookie) {
            this.props.cookies.set('maarticlefontsize', fontSize, {path: '/', maxAge: new Date(new Date() * 2).getTime()});
        }

        // Update State
        if (this.state.fontSize !== fontSize) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({fontSize});
        }
    };

    openModal = videoId => {
        const {title} = this.props;
        if (videoId && videoId.id) {
            trackEvent('Video', 'Play', `${stripTags(title)} - ${videoId.type}:${videoId.id}`);
            this.setState({modalIsOpen: true});
        }
    };

    render() {
        const {id, type, entityMetatags, title, subtitle, category, content, keyword, knowledgeCenterItems, supportingImage, hideImageFromArticle, link, ctas, fieldArticleComponents, articleViewTitle, fontOverride, overrideVideoTitle} = this.props;

        const metaTitle = this.getMetaTitle();
        const subHeadline = this.getSubHeadline();
        const videoId = link && link.uri ? ReactVideoModal.getVideoId(link.uri) : null;

        // Allow fot Font Overrides
        let additionalClasses = '';
        if (fontOverride && fontOverride === 'japanese') additionalClasses += 'is-jp';

        const keywords = Article.getKeywords(knowledgeCenterItems);

        return (
            <div id={`#twArticle${this.props.id}`} className={`${additionalClasses} articlePage articlePage--fontSize-${this.state.fontSize || 'small'} ${!type || (type && ['knowledge', 'unlisted'].includes(type)) ? 'articlePage--knowledgeMain' : ''}`}>
				
                {videoId && (
                    <ReactVideoModal inline={false} eventMetaTitle={metaTitle} uri={link.uri} isOpen={this.state.modalIsOpen} onClose={() => this.setState({modalIsOpen: false})} />
                )}

                <Helmet>
                    {metaTitle && (
                        <title>{metaTitle}</title>
                    )}

                    {Object.values(entityMetatags).length && entityMetatags.filter(metaTag => metaTag.key !== 'title' && !metaTag.key.includes('og:')).map(metaTag => (
                        <meta name={metaTag.key} content={metaTag.value} key={`standardMeta_${metaTag.key}`}/>
                    ))}
                    {Object.values(entityMetatags).length && entityMetatags.filter(metaTag => metaTag.key.includes('og:')).map(metaTag => (
                        <meta property={metaTag.key} content={metaTag.value} key={`opengraphMeta_${metaTag.key}`}/>
                    ))}
                </Helmet>

                {!type || (type && ['knowledge', 'unlisted'].includes(type)) ? (
                    <Headroom
                        className="knowledgeCenter__subnav"
                        style={{
                            zIndex: '9998',
                        }}
                    >

                        <KnowledgeCenterTopicsNav />

                    </Headroom>
                ) : null}

                {/* if knowledge center article add class nameto articlePage__container */}

                <div className={`container articlePage__container ${!type || (type && ['knowledge', 'unlisted'].includes(type)) ? 'articlePage--knowledge' : ''}`}>

                    {type && type === 'career' ? (
                        <CareersSidebar categories={knowledgeCenterItems.map(item => item.category).filter((value, index, self) => self.indexOf(value) === index)}
                                        keywords={keywords}
                                        handleChangeOnClick={(selectedTitle) => window.location.href = `/careers/stories/?title=${selectedTitle}`}
                                        handleCategoryChange={(e, selectedCategory) => window.location.href = `/careers/stories/?category=${selectedCategory}`}
                                        toggleKeyword={(e, selectedKeyword) => window.location.href = `/careers/stories/?keyword=${selectedKeyword}`}
                                        textFilterType="button"
                                        textFilter={this.state.titleSearch}
                                        categoryFilter={[category]}
                                        handleChange={(event) => this.setState({titleSearch: event.target.value})}

                        />
                    ) : null}

                    <div className="main">
                        <div className="container">

                                <React.Fragment>
                                    <h1 className={`articlePage__title ${subtitle ? 'articlePage__title--withSubtitle' : ''}`}>{articleViewTitle || title}</h1>
                                    {subtitle ? (<h2 className="articlePage__subtitle">{subtitle}</h2>) : null}
                                </React.Fragment>

                            {!hideImageFromArticle && !videoId && supportingImage && supportingImage.entity && supportingImage.entity.fieldMediaImage && supportingImage.entity.fieldMediaImage.url && (
                                <img src={supportingImage.entity.fieldMediaImage.url} alt={supportingImage.entity.fieldMediaImage.alt}/>
                            )}
                            {!hideImageFromArticle && videoId && supportingImage && supportingImage.entity && supportingImage.entity.fieldMediaImage && supportingImage.entity.fieldMediaImage.url && (
                                <button className="articlePage__videoImageWrapper" onClick={() => this.openModal(videoId)}>
                                    <img src={supportingImage.entity.fieldMediaImage.url} alt={supportingImage.entity.fieldMediaImage.alt}/>
                                </button>
                            )}

                            <div className="articlePage__subheader">
                                {subHeadline && (
                                    <h4 className="articlePage__subtitle">
                                        {subHeadline}
                                    </h4>
                                )}

                                <div className="articlePage__fontSize">
                                    <div className="articlePage__printButton">
                                        <button onClick={() => window.print()}><span className="far fa-print"/></button>
                                        <p>Print</p>
                                    </div>
                                    <div className="articlePage__fontSizeButtons">
                                        <button className={`small${this.state.fontSize === 'small' ? ' active' : ''}`} onClick={() => this.changeFontSize('small')}><span className="far fa-font"/></button>
                                        <button className={`medium${this.state.fontSize === 'medium' ? ' active' : ''}`} onClick={() => this.changeFontSize('medium')}><span className="far fa-font"/></button>
                                        <button className={`large${this.state.fontSize === 'large' ? ' active' : ''}`} onClick={() => this.changeFontSize('large')}><span className="far fa-font"/></button>
                                    </div>
                                    <p>Text</p>
                                </div>
                            </div>

                            {content && content.processed && (
                                <div className="articlePage__content">
                                    {filterHTML(content.processed, true)}
                                </div>
                            )}

                            {fieldArticleComponents.length ? (
                                <div className="articlePage__components">
                                    {fieldArticleComponents.map(component => (
                                        <Paragraph key={`${id}_${component.targetRevisionId}`}
                                                   id={String(component.targetRevisionId)}/>
                                    ))}
                                </div>
                            ) : null}

                            {videoId && !(supportingImage && supportingImage.entity && supportingImage.entity.fieldMediaImage && supportingImage.entity.fieldMediaImage.url) ? (
                                <button className="text-cta kc__videoLink" onClick={() => this.openModal(videoId)}>
                                    {overrideVideoTitle}
                                </button>
                            ) : null}

                            {ctas.length ? (
                                <div className="articlePage__links">
                                    {ctas.map(component => (
                                        <Paragraph key={`${id}_${component.targetRevisionId}`}
                                                   id={String(component.targetRevisionId)}/>
                                    ))}
                                </div>
                            ) : null}

                            {this.props.id ? (
                                <div id="twexternal">
                                    {this.state.components ? filterHTMLTwill(this.state.components, true, false, false, false) : null}
                                </div>
                            ) : null}
                            
                            {keyword && keyword.length ? (
                                <div className="articlePage__keywords">
                                    {keyword.map(keywordItem => (
                                        <a href={`/knowledge/?keyword=${keywordItem}`} key={`articlepage_keyword_${keywordItem}`} className="articlePage__keyword">
                                            {keywordItem}
                                        </a>
                                    ))}
                                </div>
                            ) : null}

                        </div>
                    </div>
                </div>

            </div>
        );
    }
}


export default withCookies(Article);



// WEBPACK FOOTER //
// ./frontend/pages/Article/Article.jsx